import React, { useState, useMemo } from 'react';
import { Select } from 'antd';
import { makeStyles } from '@material-ui/core';
import { DownOutlined } from '@ant-design/icons';
import { CreateNotification, Notification, NotificationType } from 'mdello-client-ts';
import Copy from 'src/component/Copy';
import InnerHeader from 'src/component/InnerHeader';
import { CStandardInput } from 'src/component/Input';
import NoxTable from 'src/component/Table';
import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import { formatDate } from 'src/helpers/millisecondsToFormattedDate';
import { useGetNotifications } from 'src/reactQuery/notifications/useGetNotifications';
import Loader from 'src/component/Loader/Loader';
import { useCreateNotifications } from 'src/reactQuery/notifications/createNotification';
import { TableNotificationDataInterface } from 'src/reactQuery/notifications/interfaces/notifications.interface';
import { useDeleteNotification } from 'src/reactQuery/notifications/useDeleteNotification';
import CButton from 'src/component/Button';
import Modal from '../../component/Modal/Modal';
import {
  NotificationPresentAt,
  NotificationTopics
} from '../../reactQuery/notifications/enums/notification.enum';

const { Option } = Select;

const useStyles = makeStyles({
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    marginTop: '16px'
  }
});

const Notifications = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[] | undefined>([]);
  const [createNotificationData, setCreateNotificationData] = useState<CreateNotification>({
    body: '',
    title: '',
    topic: '',
    type: NotificationType.IN_APP
  });
  const { data, isLoading } = useGetNotifications();
  const { mutate } = useCreateNotifications({});
  const { mutate: deleteNotificationMutation } = useDeleteNotification({});
  const classes = useStyles();

  const showModalHandler = () => {
    setShowModal(true);
  };

  const filteredData: TableNotificationDataInterface[] | undefined = useMemo(() => {
    if (!data) {
      return;
    }
    const filteredNotifications: TableNotificationDataInterface[] | undefined = data?.data.map(
      (notification) => {
        // @ts-ignore
        const { _id, body, createdAt, title, topic, type, presentAt } = notification;
        return {
          _id,
          body,
          createdAt,
          key: _id,
          presentAt: presentAt || '',
          title,
          topic,
          type
        };
      }
    );

    return filteredNotifications;
  }, [data?.data]);

  const columns = [
    {
      dataIndex: '_id',
      key: '+id',
      render: (_id: string) => {
        return <Copy text={_id} />;
      },
      sorter: (a: Notification, b: Notification) => a._id.localeCompare(b._id),
      title: 'ID'
    },
    {
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => {
        return <Copy text={title} />;
      },
      sorter: (a: Notification, b: Notification) => a.title.localeCompare(b.title),
      title: 'Title'
    },
    {
      dataIndex: 'body',
      key: 'body',
      render: (body: string) => {
        return <Copy text={body} />;
      },
      sorter: (a: Notification, b: Notification) => a.body.toString().localeCompare(b.body),
      title: 'Description'
    },
    {
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        return <Copy text={type} />;
      },
      sorter: (a: Notification, b: Notification) => a.type.toString().localeCompare(b.type),
      title: 'Type'
    },
    {
      dataIndex: 'topic',
      key: 'topic',
      render: (topic: string) => {
        return <Copy text={topic} />;
      },
      sorter: (a: Notification, b: Notification) => a.topic.toString().localeCompare(b.topic),
      title: 'Topic'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: Date) => {
        return <Copy text={formatDate(createdAt)?.[0] || ''} />;
      },
      sorter: (a: Notification, b: Notification) =>
        a.createdAt.toString().localeCompare(b.createdAt.toString()),
      title: 'Created At'
    }
  ];

  const onCancel = () => {
    setShowModal(false);
  };

  const onContentSave = () => {
    if (!createNotificationData) {
      return;
    }

    mutate(createNotificationData);
    setShowModal(false);
  };

  const onNotificationDelete = async () => {
    selectedRowKeys?.forEach((item: number) => {
      deleteNotificationMutation(item);
    });
  };

  const actions = [
    {
      onClick: showModalHandler,
      title: 'Create New Notification',
      type: 'primary'
    }
  ];

  if (selectedRowKeys?.length) {
    actions.unshift({
      onClick: onNotificationDelete,
      title: 'Delete',
      type: 'primary'
    });
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={data?.paging?.total} title="Notifications" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={filteredData}
        loading={isLoading}
        searchBy={SearchBy.TITLE}
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={data?.paging?.total}
      />

      <Modal
        footerActions={
          <>
            <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton onClick={onContentSave} type="primary" xstyle={{ height: '40px' }}>
              Add
            </CButton>
          </>
        }
        handleClose={onCancel}
        isModalOpen={showModal}
        styles={{
          // @ts-ignore
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingBottom: '24px',
            paddingTop: '12px'
          },
          width: '400px'
        }}
        titleContent={<h2 className="modal-title">New Content Notification</h2>}
      >
        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(e: NotificationTopics) => {
            setCreateNotificationData({ ...createNotificationData, topic: e });
          }}
          // defaultValue="Topic"
          suffixIcon={
            <DownOutlined
              rev={undefined}
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          // @ts-ignore
          value={NotificationTopics?.type}
        >
          {Object.values(NotificationTopics).map((type) => (
            <Option key={type}>{type}</Option>
          ))}
        </Select>
        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(e: NotificationPresentAt) => {
            // @ts-ignore
            setCreateNotificationData({ ...createNotificationData, presentAt: e });
          }}
          // defaultValue="Topic"
          suffixIcon={
            <DownOutlined
              rev={undefined}
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          // @ts-ignore
          value={NotificationPresentAt?.type}
        >
          {Object.values(NotificationPresentAt).map((presentAt) => (
            <Option key={presentAt}>{presentAt}</Option>
          ))}
        </Select>
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-title"
          id="standard-input-push-notification-title"
          label="Title"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCreateNotificationData({ ...createNotificationData, title: e.target.value });
          }}
          size="large"
          // value={createNotificationData?.title}
          width="true"
        />
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-text"
          id="standard-input-push-notification-text"
          label="Notification"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCreateNotificationData({ ...createNotificationData, body: e.target.value });
          }}
          size="large"
          // value={createNotificationData?.text}
          width="true"
        />
      </Modal>
    </div>
  );
};

export default Notifications;
